import React, { useEffect, useMemo, useState } from "react";
import {
  GetProduct as FacilityGetProduct,
  TExtendedProductFilters,
  TGetProduct,
} from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetProduct as ClientGetProduct } from "api/rpc/2024-04/clientAdmin/product/product";
import "./productSelect.scss";
import axios, { CancelToken } from "axios";
import { IProduct, IVariant, TProductVendor } from "redux/reducers/models/product";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Search from "components/search/Search";
import classNames from "classnames";
import Spin from "components/spin/spin";
import Checkbox from "components/form/checkbox/Checkbox";
import Callout from "components/callout/Callout";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import DataTable from "components/dataTable/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { cloneDeep } from "lodash";
import { UserType } from "redux/reducers/models/user";
import DropFilter from "components/dropdown/DropFilter";
import { GetVendor as GetVendorFacility } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetVendor as GetVendorClient } from "api/rpc/2022-09/clientAdmin/product/vendors";
import { useTranslation } from "react-i18next";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";

interface IProductExtended extends IProduct {
  variants: Array<IVariantExtended>;
}

interface IVariantExtended extends IVariant {
  [key: string]: any;
}

type TVariantInput = {
  key: string;
  placeholder?: string;
};
interface IProps {
  /**Array of products selected in state*/
  selectedProducts: Array<IProductExtended>;
  /**Function that returns the updated selected variants */
  onChange: (variants: Array<IProductExtended>) => void;
  /**(Optional) Display search input to search products */
  search?: boolean;
  /**(Optional) Function that runs when search is performed. Returns search string*/
  onSearch?: (search: string) => void;
  /**(Optional) Parameters for GET Product endpoint*/
  getProductsParams?: Partial<Omit<TGetProduct, "search" | "limit" | "offset" | "variants">>;
  /**(Optional) Sets the maximum height of the products table*/
  maxHeight?: string;
  /**(Optional) Object that contains the variant input key and optional placeholder*/
  variantInput?: TVariantInput;
  /**(Optional) Limit the number of products returned to this value*/
  limit?: number;
  /**(Optional) Display vendors filter to filter products by vendor */
  vendorFilter?: boolean;
  /**(Optional) Variant IDs to hide from the list (e.g. already added to a sheet) */
  excludedVariantIds?: number[];
  /**(Optional) Hide all variant rows and select parent products only */
  hideVariants?: boolean;
  /**(Optional) Hide variants that do not match this predicate */
  filterVariants?: (variant: IVariant) => boolean;
}

interface IProductState {
  products: Array<IProductExtended>;
  searchQuery: string;
}

interface IFilters {
  label: keyof TExtendedProductFilters;
  value: Array<any>;
}

/**
 * Component that allows products to be searched and selected. Returns an array of variants
 * @param {IProps} props
 * @param {boolean} props.selectedProducts Array of products selected in state
 * @param {(variants: Array<IVariant>) => void} props.onChange Function that returns the updated selected variants
 * @param {boolean} props.search `(Optional)` Display search input to search products
 * @param {(search: string) => void} props.onSearch `(Optional)`Function that runs when search is performed. Returns search string
 * @param {TGetProduct} props.getProductsParams `(Optional)` Parameters for GET Product endpoint
 * @param {CSSHeight} props.maxHeight `(Optional)` Sets the maximum height of the products table
 * @param {TVariantInput} props.variantInput `(Optional)` Object that contains the variant input key and optional placeholder
 * @param {number} props.limit `(Optional)` Limit the number of products returned to this value
 * @param {boolean} props.vendorFilter `(Optional)` Display vendors filter to filter products by vendor
 * @param {number[]} props.excludedVariantIds `(Optional)` Variant IDs to hide from the list
 * @param {boolean} props.hideVariants `(Optional)` Hide all variant rows and select parent products only
 * @param {(variant: IVariant) => boolean} props.filterVariants `(Optional)` Hide variants that do not match this predicate
 * @returns {JSX.Element}
 */
export default function ProductSelect(props: IProps): JSX.Element {
  const {
    selectedProducts,
    onChange,
    search,
    getProductsParams,
    maxHeight,
    onSearch,
    variantInput,
    limit,
    vendorFilter,
    excludedVariantIds,
    hideVariants,
    filterVariants,
  } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const authStore = useAppSelector(store => store.authStore);
  const facilityStore = useAppSelector(store => store.facilityStore);
  const { getCurrencySymbol } = useCurrencyFacility();
  const currencySymbol = getCurrencySymbol(facilityStore?.facility?.default_currency_code ?? "CAD");
  const [productState, setProductState] = useState<IProductState>({
    products: undefined,
    searchQuery: "",
  });
  const [vendors, setVendors] = useState<Array<TProductVendor>>(null);
  const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(new Set());

  const [filters, setFilters] = useState<Array<IFilters>>([]);
  const [offset, setOffset] = useState<number>(0);

  const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds ?? []), [excludedVariantIds]);

  const displayProducts = useMemo(() => {
    if (!productState.products) {
      return undefined;
    }

    if (excludedVariantIdSet.size === 0 && !filterVariants) {
      return productState.products;
    }

    return productState.products
      .map(product => {
        const variants = (product.variants ?? []).filter(variant => {
          if (excludedVariantIdSet.has(variant.id)) {
            return false;
          }
          if (filterVariants && !filterVariants(variant)) {
            return false;
          }
          return true;
        });
        if (variants.length === 0) {
          return null;
        }

        return {
          ...product,
          variants,
          variant_count: variants.length,
        };
      })
      .filter((product): product is IProductExtended => product != null);
  }, [productState.products, excludedVariantIdSet, filterVariants]);

  const tableColumns = useMemo(() => {
    const columns = [{ label: t("Name") }, { label: t("Type") }];
    if (!hideVariants) {
      columns.push({ label: variantInput ? t("Quantity") : t("Price") });
    }
    return columns;
  }, [t, variantInput, hideVariants]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (vendorFilter) {
      void loadVendors(source.token);
    }
    return () => source.cancel();
  }, [vendorFilter]);

  const getProductsParamsKey = JSON.stringify(getProductsParams ?? {});

  useEffect(() => {
    setOffset(0);
  }, [getProductsParamsKey]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(productState.searchQuery, source.token);
    return () => source.cancel();
  }, [productState.searchQuery, offset, filters, getProductsParamsKey]);

  async function loadVendors(cancelToken: CancelToken) {
    const userType = authStore?.user?.user_type;
    if (userType === UserType.FACILITY_ADMIN) {
      const res = await GetVendorFacility(null, false);
      if (res?.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting vendors")));
        return;
      }
      setVendors(res?.data);
    } else if (userType === UserType.CLIENT_ADMIN) {
      const res = await GetVendorClient(null, false);
      if (res?.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting vendors")));
        return;
      }
      setVendors(res?.data);
    }
  }

  async function loadProducts(search: string, token: CancelToken) {
    const userType = authStore?.user?.user_type;
    if (productState.products !== undefined) {
      setProductState(prevState => ({ ...prevState, products: undefined }));
    }

    const filter = {
      offset: limit ? offset : undefined,
      limit: limit ? limit : undefined,
    };

    if (userType === UserType.CLIENT_ADMIN) {
      const res = await ClientGetProduct(
        {
          search: search ?? undefined,
          variants: true,
          ...getProductsParams,
          ...filter,
          filters: filters && filters?.length > 0 ? filters : undefined,
        },
        false,
        token,
      );

      if (res.status !== StatusCode.OK) {
        if (token?.reason) {
          return;
        }
        dispatch(showError(t("Error loading products")));
      }
      setProductState(prevState => ({ ...prevState, products: res.status !== StatusCode.OK ? [] : res.data }));
    } else if (userType === UserType.FACILITY_ADMIN) {
      const res = await FacilityGetProduct(
        {
          search: search ?? undefined,
          variants: true,
          ...getProductsParams,
          ...filter,
          filters: filters && filters?.length > 0 ? filters : undefined,
        },
        false,
        token,
      );

      if (res.status !== StatusCode.OK) {
        if (token?.reason) {
          return;
        }
        dispatch(showError(t("Error loading products")));
      }
      setProductState(prevState => ({ ...prevState, products: res.status !== StatusCode.OK ? [] : res.data }));
    } else {
      dispatch(showError(t("User is not authorized to access this page")));
    }
  }

  function handleSelectProduct(productIndex: number, variantIndex?: number) {
    if (!displayProducts) {
      return;
    }

    const updatedSelectedProducts = [...selectedProducts];
    const currentProduct = cloneDeep(displayProducts[productIndex]);
    const foundProductIndex = updatedSelectedProducts?.findIndex(
      selectedProduct => selectedProduct?.id === currentProduct?.id,
    );

    if (hideVariants) {
      if (foundProductIndex === -1) {
        updatedSelectedProducts.push({ ...currentProduct });
      } else {
        updatedSelectedProducts.splice(foundProductIndex, 1);
      }
      void onChange(updatedSelectedProducts);
      return;
    }

    if (variantIndex != null) {
      const currentVariant = currentProduct?.variants[variantIndex];
      // Single variant selected
      if (foundProductIndex === -1) {
        // Add product and single variant
        const updatedCurrentProduct = { ...currentProduct, variants: [{ ...currentVariant }] };
        updatedSelectedProducts.push(updatedCurrentProduct);
      } else {
        const foundVariantIndex = updatedSelectedProducts[foundProductIndex]?.variants?.findIndex(
          selectedVariant => selectedVariant?.id === currentVariant?.id,
        );
        // Add single variant
        if (foundVariantIndex === -1) {
          updatedSelectedProducts[foundProductIndex] = {
            ...updatedSelectedProducts[foundProductIndex],
            variants: [...updatedSelectedProducts[foundProductIndex].variants, currentVariant],
          };
        } else {
          updatedSelectedProducts[foundProductIndex]?.variants?.splice(foundVariantIndex, 1);
          if (updatedSelectedProducts[foundProductIndex]?.variants?.length === 0) {
            updatedSelectedProducts.splice(foundProductIndex, 1);
          }
        }
      }
    } else {
      // All variants selected
      if (foundProductIndex === -1) {
        // Product not found, add product + all its variants
        updatedSelectedProducts.push({ ...currentProduct });
      } else {
        // Product found
        if (
          currentProduct?.variants?.every(currentVariant =>
            updatedSelectedProducts[foundProductIndex]?.variants?.some(variant => variant?.id === currentVariant?.id),
          )
        ) {
          // Every remove product and all variants
          updatedSelectedProducts.splice(foundProductIndex, 1);
        } else {
          // Add missing variants
          currentProduct?.variants?.forEach(currentVariant => {
            if (
              !updatedSelectedProducts[foundProductIndex]?.variants?.find(
                variant => variant?.id === currentVariant?.id,
              )
            ) {
              updatedSelectedProducts[foundProductIndex]?.variants?.push({ ...currentVariant });
            }
          });
        }
      }
    }
    void onChange(updatedSelectedProducts);
  }

  function handleSearch(searchValue: string) {
    if (onSearch) {
      void onSearch(searchValue);
    }
    setOffset(0);
    setProductState(prevState => ({ ...prevState, searchQuery: searchValue }));
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>, productId: number, variantId: number) {
    const { value } = e.target;
    const products = [...selectedProducts];
    const productIndex = products?.findIndex(product => product.id === productId);
    if (productIndex !== -1) {
      const variants = products[productIndex]?.variants;
      const variantIndex = variants?.findIndex(variant => variant?.id === variantId);
      if (variantIndex !== -1) {
        variants[variantIndex] = { ...variants[variantIndex], [variantInput.key]: value };
        void onChange(products);
        return;
      }
      console.error(`VariantId Id: ${variantId} not found`);
    }
    console.error(`ProductId Id: ${productId} not found`);
  }

  function handleUpdateFilters(filterData: Array<Record<string, any>>, key: keyof TExtendedProductFilters) {
    let updatedFilters = [...filters];
    if (filterData?.length === 0) {
      updatedFilters = updatedFilters?.filter(filter => filter?.label !== key);
    } else {
      const foundIndex = updatedFilters?.findIndex(filter => filter?.label === key);
      if (foundIndex !== -1) {
        updatedFilters[foundIndex] = {
          label: key,
          value: filterData?.map((data: Record<string, any>) => data?.value as number),
        };
      } else {
        updatedFilters = [
          ...updatedFilters,
          { label: key, value: filterData?.map((data: Record<string, any>) => data?.value as number) },
        ];
      }
    }
    setOffset(0);
    setFilters(updatedFilters);
  }

  function toggleProductExpanded(productId: number) {
    setExpandedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  function formatVariantPrice(variant: IVariant) {
    if (variant?.price == null) {
      return "";
    }

    return `${currencySymbol}${Number(variant.price).toFixed(2)}`;
  }

  function getVariantCountLabel(variantCount: number) {
    return variantCount === 1 ? t("1 variant") : t("{{count}} variants", { count: variantCount });
  }

  function getProductDisplayTitle(product: IProductExtended) {
    const productTitle = product?.preferred_title ? product?.preferred_title : product.title;
    const variantTitle = hideVariants ? null : product.variants?.[0]?.title?.trim();

    if (product.variant_count === 1 && variantTitle && variantTitle !== productTitle && variantTitle !== "Default") {
      return `${productTitle} - ${variantTitle}`;
    }

    return productTitle;
  }

  function getProductSecondaryLabel(product: IProductExtended, multiVariant: boolean) {
    if (multiVariant) {
      return getVariantCountLabel(product.variant_count);
    }

    if (product?.subtitle?.trim()) {
      return product.subtitle.trim();
    }

    return (product?.vendor_title ?? "") as string;
  }

  function handleActivationKeyDown(event: React.KeyboardEvent, action: () => void) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  }

  const selectedCount = hideVariants
    ? (selectedProducts?.length ?? 0)
    : selectedProducts?.reduce((total, product) => total + (product.variants?.length ?? 0), 0);

  return (
    <div className="product-select">
      {vendorFilter && vendors ? (
        <>
          <DropFilter
            title={t("Vendor")}
            filterData={vendors?.map(vendor => ({ title: vendor?.title, value: vendor?.id }))}
            filterLabelPropFromData="title"
            filterIdPropFromData="value"
            filterType="Checkbox"
            applyFilters={filters => handleUpdateFilters(filters, "vendor_id")}
            save
            historyKey="product_select_vendors_filter"
            leftAligned
          />
          <br />
        </>
      ) : null}

      {search && (
        <Search
          searchCallback={searchValue => handleSearch(searchValue)}
          historyKey="inventory_product_modal"
          placeholder={hideVariants ? t("Search products...") : t("Search products or variants...")}
          icon={faMagnifyingGlass}
        />
      )}

      <div className="product-select-table" style={{ maxHeight: maxHeight ?? undefined }}>
        {displayProducts ? (
          displayProducts.length > 0 ? (
            <DataTable className="product-select__table" columns={tableColumns} stickyHeader>
              {displayProducts.map((product, productIndex) => {
                const singleVariant = product?.variant_count === 1;
                const multiVariant = !hideVariants && product?.variant_count > 1;
                const isExpanded = !hideVariants && expandedProductIds.has(product.id);
                const foundProduct = selectedProducts?.find(selectedProduct => selectedProduct?.id === product?.id);
                const foundSingleVariant = foundProduct?.variants?.find(
                  variant => variant?.id === product?.variants[0]?.id,
                );
                const productTitle = getProductDisplayTitle(product);
                const allVariantsSelected = hideVariants
                  ? foundProduct != null
                  : product?.variants?.every(productVariant =>
                      foundProduct?.variants?.find(variant => variant?.id === productVariant?.id),
                    ) ?? false;
                const someVariantsSelected =
                  !hideVariants &&
                  !allVariantsSelected &&
                  (product?.variants?.some(productVariant =>
                    foundProduct?.variants?.find(variant => variant?.id === productVariant?.id),
                  ) ??
                    false);
                const secondaryLabel = getProductSecondaryLabel(product, product?.variant_count > 1);

                return (
                  <React.Fragment key={product.id}>
                    <tr
                      className="product-select__product-row clickable"
                      tabIndex={0}
                      aria-selected={allVariantsSelected}
                      onClick={() => handleSelectProduct(productIndex, hideVariants || !singleVariant ? undefined : 0)}
                      onKeyDown={event =>
                        handleActivationKeyDown(event, () =>
                          handleSelectProduct(productIndex, hideVariants || !singleVariant ? undefined : 0),
                        )
                      }
                    >
                      <td>
                        <div className="product-select__product-cell">
                          <span aria-hidden="true">
                            <Checkbox
                              size="medium"
                              labelHidden
                              checked={allVariantsSelected}
                              indeterminate={someVariantsSelected}
                              readOnly
                              tabIndex={-1}
                            />
                          </span>
                          <div className="product-select__product-text">
                            <div className="product-select__product-name">{productTitle}</div>
                            {secondaryLabel ? (
                              <div className="product-select__product-label">{secondaryLabel}</div>
                            ) : null}
                          </div>
                          {multiVariant ? (
                            <button
                              type="button"
                              className="product-select__chevron"
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? t("Collapse") : t("Expand")}
                              onClick={e => {
                                e.stopPropagation();
                                toggleProductExpanded(product.id);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={isExpanded ? ["far", "chevron-up"] : ["far", "chevron-down"]}
                              />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="product-select__type-cell">{product.type}</td>
                      {!hideVariants ? (
                        <td className="product-select__meta-cell">
                          {singleVariant && variantInput ? (
                            <div onClick={e => e.stopPropagation()}>
                              <Input
                                placeholder={variantInput.placeholder}
                                type="number"
                                disabled={!foundSingleVariant}
                                value={foundSingleVariant ? foundSingleVariant[variantInput.key] ?? "" : ""}
                                labelHidden
                                onChange={e => onInputChange(e, product?.id, product?.variants[0]?.id)}
                              />
                            </div>
                          ) : singleVariant ? (
                            formatVariantPrice(product.variants[0])
                          ) : null}
                        </td>
                      ) : null}
                    </tr>

                    {multiVariant && isExpanded
                      ? product?.variants?.map((variant, variantIndex: number) => {
                          const foundVariant = foundProduct?.variants?.find(
                            selectedVariant => selectedVariant?.id === variant?.id,
                          );
                          return (
                            <tr
                              key={variant.id}
                              className="product-select__variant-row clickable"
                              tabIndex={0}
                              aria-selected={Boolean(foundVariant)}
                              onClick={() => handleSelectProduct(productIndex, variantIndex)}
                              onKeyDown={event =>
                                handleActivationKeyDown(event, () => handleSelectProduct(productIndex, variantIndex))
                              }
                            >
                              <td>
                                <div
                                  className={classNames(
                                    "product-select__product-cell",
                                    "product-select__product-cell--variant",
                                  )}
                                >
                                  <span aria-hidden="true">
                                    <Checkbox
                                      size="medium"
                                      labelHidden
                                      checked={Boolean(foundVariant)}
                                      readOnly
                                      tabIndex={-1}
                                    />
                                  </span>
                                  <div className="product-select__product-text">
                                    <div className="product-select__product-name">{variant?.title}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="product-select__type-cell" />
                              <td className="product-select__meta-cell">
                                {variantInput ? (
                                  <div onClick={e => e.stopPropagation()}>
                                    <Input
                                      placeholder={variantInput.placeholder}
                                      type="number"
                                      disabled={!foundVariant}
                                      value={foundVariant ? foundVariant[variantInput.key] ?? "" : ""}
                                      labelHidden
                                      onChange={e => onInputChange(e, product.id, variant.id)}
                                    />
                                  </div>
                                ) : (
                                  formatVariantPrice(variant)
                                )}
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })}
            </DataTable>
          ) : (
            <Callout type="info" title={t("No products found")} />
          )
        ) : (
          <div className="spinner">
            <Spin />
          </div>
        )}
      </div>

      <div className="product-select-footer">
        {selectedCount > 0 ? (
          <p className="product-select-summary">
            {selectedCount === 1
              ? t("1 product selected")
              : t("{{count}} products selected", { count: selectedCount })}
          </p>
        ) : null}

        {!!limit && productState.products ? (
          <div className="product-select-pagination">
            <Button
              size="medium"
              type="secondary"
              onClick={() => setOffset(prev => prev - limit)}
              disabled={!(limit <= offset)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              &nbsp;
              {t("Prev")}
            </Button>
            <Button
              size="medium"
              type="secondary"
              onClick={() => setOffset(prev => prev + limit)}
              disabled={!productState.products || !(productState?.products?.length === limit)}
            >
              {t("Next")}
              &nbsp;
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
